"use client";

import { ProductBadges } from "@/ui/components/catalog/product-badges";
import Link from "next/link";
import Image from "next/image";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { ProductQualityNote } from "@/ui/components/catalog/product-quality-note";
import { FavoriteButton } from "@/ui/components/favorite-button";
import { ProductCardCart } from "./product-card-cart";

export interface ProductCardVariant {
	id: string;
	name: string;
	quantityAvailable: number | null;
	price: number | null;
	currency: string | null;
}

export interface ProductCardData {
	id: string;
	name: string;
	slug: string;
	brand?: string | null;
	price: number;
	compareAtPrice?: number | null;
	currency: string;
	/** Set with `pricePresentation` when using reference currency UX */
	locale?: string;
	channel?: string;
	pricePresentation?: ResolvedMoneyPresentation;
	compareAtPresentation?: ResolvedMoneyPresentation | null;
	vialsPerSaleUnit?: number | null;
	perVialPresentation?: ResolvedMoneyPresentation | null;
	isStartingPrice?: boolean;
	image: string;
	imageAlt?: string;
	hoverImage?: string | null;
	href: string;
	badge?: "Sale" | "New" | null;
	colors?: { name: string; hex: string }[];
	sizes?: string[];
	category?: { id: string; name: string; slug: string; translation?: { name: string } | null } | null;
	variants?: ProductCardVariant[];
	createdAt?: string | null;
	hasVariants?: boolean;
	onQuickAdd?: (productId: string) => void;
	shortDescription?: string | null;
	trustBadges?: string[];
	reviewSummary?: { avgRating: number; count: number } | null;
}

interface ProductCardProps {
	product: ProductCardData;
	priority?: boolean;
}

export function ProductCard({ product, priority = false }: ProductCardProps) {
	return (
		<article className="hover:border-primary/30 group relative flex h-full flex-col overflow-hidden border border-border bg-card transition-all duration-300 hover:shadow-md">
			{/* Blueprint-Raster overlay */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]"
				style={{
					backgroundImage:
						"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
					backgroundSize: "24px 24px",
				}}
			/>

			{/* Favorite (Bookmark) — sichtbar auf allen Breakpoints */}
			<div
				className="absolute right-2.5 top-2.5 z-10"
				onClick={(e) => e.preventDefault()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<FavoriteButton
					slug={product.slug}
					className="bg-background/80 backdrop-blur-sm hover:bg-background"
				/>
			</div>

			<Link href={product.href} className="relative flex flex-1 flex-col">
				<div
					className={`product-card-image-swap relative aspect-square overflow-hidden bg-white ${product.hoverImage ? "product-card-image-swap--has-hover" : ""}`}
				>
					<Image
						src={product.image}
						alt={product.imageAlt || product.name}
						fill
						sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
						className="product-card-image-primary object-contain object-center transition duration-200 ease-out"
						priority={priority}
					/>
					{product.hoverImage && (
						<Image
							src={product.hoverImage}
							alt=""
							fill
							sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
							className="product-card-image-hover object-contain object-center opacity-0 transition duration-200 ease-out"
						/>
					)}
					{product.badge && (
						<span className="bg-foreground/85 absolute left-0 top-3 rounded-r-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-background backdrop-blur-sm">
							{product.badge}
						</span>
					)}
				</div>

				<div className="flex flex-1 flex-col p-3.5 pt-3">
					{product.brand && (
						<p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
							{product.brand}
						</p>
					)}
					<h3 className="group-hover:text-primary line-clamp-2 text-[15px] font-semibold uppercase leading-snug tracking-[0.04em]">
						{product.name}
					</h3>
					<ProductQualityNote slug={product.slug} />
					{product.shortDescription && (
						<p className="line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
							{product.shortDescription}
						</p>
					)}
					{product.reviewSummary && product.reviewSummary.count > 0 && (
						<div className="flex items-center gap-1">
							<svg
								className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
								viewBox="0 0 20 20"
								aria-hidden="true"
							>
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
							<span className="text-xs font-medium tabular-nums text-foreground">
								{product.reviewSummary.avgRating.toFixed(1)}
							</span>
							<span className="text-xs text-muted-foreground">({product.reviewSummary.count})</span>
						</div>
					)}
					<ProductBadges trustBadges={product.trustBadges} slug={product.slug} category={product.category} />
				</div>
			</Link>

			{/* Purchase action */}
			<div className="p-2 sm:p-3">
				<ProductCardCart
					channel={product.channel}
					locale={product.locale}
					productName={product.name}
					variants={product.variants ?? []}
					pricing={product}
				/>
			</div>
		</article>
	);
}
